import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Smartphone } from 'lucide-react';
import { format } from 'date-fns';
import type { Message } from '@/types/message-types';

interface FlowScreenPreviewProps {
  message: Message;
}

/**
 * V1208: FlowScreenPreview
 *
 * Displays WhatsApp Flow screen interactions in the chat UI.
 * Shows a card with the screen name and submitted values.
 *
 * Content format (V1208):
 * {
 *   screen: "SCREEN_ID",
 *   data: { field: value, ... },        // User's raw input
 *   context: {                          // Rich context from response.data
 *     price_display: "Precio: $89.990", // Pre-formatted with label from schema
 *     product_name: "...",              // Product details
 *     cart_summary: "...",              // Formatted cart
 *     ...
 *   }
 * }
 *
 * V1208: Zero hardcoding - all labels come from flow_json schema via _display fields
 */
export function FlowScreenPreview({ message }: FlowScreenPreviewProps) {
  const { t } = useTranslation('conversations');

  // Parse content JSON
  const content = React.useMemo(() => {
    try {
      return JSON.parse(message.content || '{}');
    } catch {
      return {};
    }
  }, [message.content]);

  const screenId = content.screen || message.metadata?.screen_id || 'Unknown';
  const data = content.data || {};
  const context = content.context || {}; // V1208: Rich context from response.data

  // V1206: Universal label formatting - converts SCREEN_ID to "Screen Id" format
  const formatScreenLabel = (id: string): string => {
    return id
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Format field values for display
  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) return value.join(', ');  // V1209: Arrays as comma-separated
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // V1210: Universal pattern-based labels - works for any e-commerce flow
  const getFieldLabel = (key: string): string => {
    const k = key.toLowerCase();
    // Universal e-commerce patterns (not tenant-specific)
    if (k.includes('product_id') || k === 'kopr' || k === 'sku') return 'SKU';
    if (k.includes('quantity') || k.includes('qty')) return 'Cantidad';
    if (k.includes('brand') || k === 'marca' || k === 'selected_brand') return 'Marca';
    if (k === 'cart_action' || k === 'action') return 'Acción';
    // Fallback: Title Case
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // V1210: Get display value with universal context enrichment
  const getDisplayValue = (key: string, value: unknown): { label: string; value: string; isFormatted: boolean } => {
    // Check if context has a _display version (e.g., "price_display" for "price")
    const displayKey = `${key}_display`;
    if (context[displayKey] && typeof context[displayKey] === 'string') {
      // _display fields include the label already (e.g., "Precio: $89.990")
      return { label: '', value: context[displayKey], isFormatted: true };
    }

    const k = key.toLowerCase();

    // V1210: Universal product ID enrichment - show product name from context
    if (k.includes('product_id') || k === 'kopr') {
      const productName = context.selected_product?.name ||
                          context.product_name ||
                          context.product?.product_name ||
                          context.cart?.items?.[0]?.product_name;
      if (productName) {
        return { label: 'SKU', value: `${formatValue(value)} - ${productName}`, isFormatted: false };
      }
    }

    // Fallback: pattern-based label + raw value
    return { label: getFieldLabel(key), value: formatValue(value), isFormatted: false };
  };

  // Filter out internal fields and empty values
  // V1209: Universal filter - keys AND values for WhatsApp internal patterns
  const displayFields = Object.entries(data)
    .filter(([k, v]) => {
      // Key filters
      if (k.startsWith('_') || k.startsWith('components-')) return false;
      if (k === 'error' || k === 'error_message') return false;
      if (k === 'RadioButtonsGroup' || k.includes('validation')) return false;
      // Value filter - catch WhatsApp internal error text in ANY field
      const valueStr = String(v);
      if (valueStr.includes('components-') || valueStr.includes('RadioButtonsGroup')) return false;
      return v !== null && v !== undefined && v !== '';
    });

  // V1208: Collect _display fields from context that aren't tied to a data field
  const contextDisplayFields = React.useMemo(() => {
    const dataKeys = new Set(Object.keys(data));
    return Object.entries(context)
      .filter(([k, v]) => {
        // Only show _display fields that aren't already shown via data fields
        if (!k.endsWith('_display')) return false;
        const baseKey = k.replace('_display', '');
        if (dataKeys.has(baseKey)) return false; // Already shown via displayFields
        if (v === null || v === undefined || v === '') return false;
        return true;
      })
      .map(([k, v]) => ({ key: k, value: String(v) }));
  }, [data, context]);

  // V1208: Show cart_summary if available (pre-formatted in cart-utils)
  const cartSummary = context.cart_summary || context.subtotal_display;

  return (
    <Card className="border-purple-200 bg-purple-50/50 max-w-sm shadow-sm">
      <CardHeader className="py-2 px-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-purple-700">
          <Smartphone className="h-4 w-4" />
          Paso en Flow
        </CardTitle>
        <p className="text-xs text-purple-600">Pantalla: {formatScreenLabel(screenId)}</p>
      </CardHeader>

      {(displayFields.length > 0 || contextDisplayFields.length > 0 || cartSummary) && (
        <CardContent className="py-2 px-3 border-t border-purple-200">
          <div className="space-y-1.5">
            {/* Render data fields with context enrichment */}
            {/* V1210: Left-aligned, no truncation, visible labels */}
            {displayFields.map(([key, value]) => {
              const display = getDisplayValue(key, value);
              return (
                <div key={key} className="text-sm">
                  {display.isFormatted ? (
                    // _display field - already has label included
                    <span className="font-medium text-purple-900">{display.value}</span>
                  ) : (
                    // Regular field with label: value format - left aligned
                    <div>
                      <span className="text-purple-600">{display.label}:</span>{' '}
                      <span className="font-medium text-purple-900 break-all">{display.value}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* V1208: Show additional _display fields from context */}
            {contextDisplayFields.map(({ key, value }) => (
              <div key={key} className="text-sm">
                <span className="font-medium">{value}</span>
              </div>
            ))}

            {/* V1208: Show cart summary if available */}
            {cartSummary && !contextDisplayFields.some(f => f.key === 'cart_summary' || f.key === 'subtotal_display') && (
              <div className="text-sm pt-1 border-t border-purple-100 mt-1">
                <span className="font-medium whitespace-pre-line">{cartSummary}</span>
              </div>
            )}
          </div>

          <div className="mt-2 text-right">
            <span className="text-xs text-muted-foreground">
              {format(new Date(message.created_at), 'HH:mm')}
            </span>
          </div>
        </CardContent>
      )}

      {displayFields.length === 0 && contextDisplayFields.length === 0 && !cartSummary && (
        <CardContent className="py-2 px-3 border-t border-purple-200">
          <p className="text-xs text-muted-foreground italic">{t('flowScreenPreview.noDataSubmitted')}</p>
          <div className="mt-2 text-right">
            <span className="text-xs text-muted-foreground">
              {format(new Date(message.created_at), 'HH:mm')}
            </span>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
